import React, { useEffect, useState } from "react";
import { feedbackApi, errorMessage, withToken } from "../adminApi";
import { useAdminToast } from "../components/Toast";
import { Card, Table, Badge, Button, Modal, Loading, ErrorState, PageHeader, Select, Pagination } from "../components/ui";
import { Icon } from "../components/icons";
import { formatDateTime } from "../format";

const PAGE_SIZE = 20;

const CATEGORY = {
  KRITIK_SARAN: { label: "Kritik & Saran", color: "blue" },
  BUG: { label: "Bug / Error", color: "red" },
  LAINNYA: { label: "Lainnya", color: "gray" },
};
const STATUS = {
  NEW: { label: "Baru", color: "yellow" },
  READ: { label: "Dibaca", color: "blue" },
  DONE: { label: "Selesai", color: "green" },
};

const cat = (k) => CATEGORY[k] || { label: k, color: "gray" };
const stat = (k) => STATUS[k] || { label: k, color: "gray" };

/** Apakah lampiran berupa gambar (untuk preview inline)? */
function isImage(url) {
  return /\.(jpe?g|png|webp|heic|gif)(\?|$)/i.test(url || "");
}

/**
 * KOTAK MASUK FEEDBACK (SUPER_ADMIN/HR_ADMIN).
 * Kritik, saran & laporan bug dari user + lampiran screenshot. Bisa filter
 * status/kategori, lihat detail, dan ubah status (Baru → Dibaca → Selesai).
 */
export default function FeedbackPage() {
  const toast = useAdminToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await feedbackApi.list(params);
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, categoryFilter]);

  // Buka detail; bila masih NEW, otomatis tandai sebagai "Dibaca".
  const openDetail = async (row) => {
    setDetail(row);
    if (row.status === "NEW") {
      try {
        await feedbackApi.updateStatus(row.id, "READ");
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: "READ" } : r)));
      } catch {
        /* diamkan: gagal auto-mark tidak fatal */
      }
    }
  };

  const changeStatus = async (row, status) => {
    setBusy(true);
    try {
      await feedbackApi.updateStatus(row.id, status);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status } : r)));
      setDetail((d) => (d && d.id === row.id ? { ...d, status } : d));
      toast.success(`Status → ${stat(status).label}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: "sender",
      header: "Pengirim",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-semibold text-slate-700 truncate">{r.sender_name || "-"}</div>
          <div className="text-[11px] text-slate-400 truncate">{r.sender_department || r.sender_username || ""}</div>
        </div>
      ),
    },
    {
      key: "category",
      header: "Jenis",
      render: (r) => <Badge color={cat(r.category).color}>{cat(r.category).label}</Badge>,
    },
    {
      key: "message",
      header: "Pesan",
      className: "min-w-[240px]",
      render: (r) => (
        <button onClick={() => openDetail(r)} className="text-left text-slate-600 hover:text-brand-600 line-clamp-2">
          {r.message}
        </button>
      ),
    },
    {
      key: "attachment",
      header: "Lampiran",
      render: (r) =>
        r.attachment_url ? (
          <span className="inline-flex items-center gap-1 text-brand-600">
            <Icon.Attachment size={16} /> Ada
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    { key: "created_at", header: "Tanggal", render: (r) => <span className="text-slate-500 whitespace-nowrap">{formatDateTime(r.created_at)}</span> },
    {
      key: "status",
      header: "Status",
      render: (r) => <Badge color={stat(r.status).color}>{stat(r.status).label}</Badge>,
    },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <Button variant="secondary" size="sm" onClick={() => openDetail(r)}>
          Detail
        </Button>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Kotak Masuk"
        subtitle="Kritik, saran & laporan bug dari karyawan"
        action={
          <div className="flex gap-2">
            <Select value={statusFilter} onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}>
              <option value="">Semua status</option>
              <option value="NEW">Baru</option>
              <option value="READ">Dibaca</option>
              <option value="DONE">Selesai</option>
            </Select>
            <Select value={categoryFilter} onChange={(e) => { setPage(0); setCategoryFilter(e.target.value); }}>
              <option value="">Semua jenis</option>
              <option value="KRITIK_SARAN">Kritik & Saran</option>
              <option value="BUG">Bug / Error</option>
              <option value="LAINNYA">Lainnya</option>
            </Select>
            <Button variant="secondary" onClick={load} disabled={loading}>
              <Icon.Refresh size={16} /> Muat ulang
            </Button>
          </div>
        }
      />

      {loading ? (
        <Loading label="Memuat kotak masuk…" />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <Card className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0">
            <Table columns={columns} rows={rows} empty="Belum ada masukan masuk." />
          </div>
          <div className="px-4 pb-3">
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} />
          </div>
        </Card>
      )}

      {/* Detail */}
      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? cat(detail.category).label : ""}
        subtitle={detail ? `${detail.sender_name || "-"} · ${formatDateTime(detail.created_at)}` : ""}
        wide
        footer={
          detail && (
            <div className="flex items-center justify-between w-full">
              <Badge color={stat(detail.status).color}>{stat(detail.status).label}</Badge>
              <div className="flex gap-2">
                {detail.status !== "DONE" && (
                  <Button variant="success" size="sm" disabled={busy} onClick={() => changeStatus(detail, "DONE")}>
                    <Icon.Check size={16} /> Tandai selesai
                  </Button>
                )}
                {detail.status === "DONE" && (
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => changeStatus(detail, "READ")}>
                    Buka kembali
                  </Button>
                )}
              </div>
            </div>
          )
        }
      >
        {detail && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 text-[12px] text-slate-500">
              <span className="rounded bg-slate-100 px-2 py-1">👤 {detail.sender_name || "-"}</span>
              {detail.sender_department && <span className="rounded bg-slate-100 px-2 py-1">🏢 {detail.sender_department}</span>}
              {detail.sender_username && <span className="rounded bg-slate-100 px-2 py-1">@{detail.sender_username}</span>}
            </div>

            <div className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{detail.message}</div>

            {detail.attachment_url && (
              <div>
                <div className="mb-1.5 text-[12px] font-semibold text-slate-500">Lampiran</div>
                {isImage(detail.attachment_url) ? (
                  <a href={withToken(detail.attachment_url)} target="_blank" rel="noreferrer">
                    <img
                      src={withToken(detail.attachment_url)}
                      alt="Lampiran"
                      className="max-h-80 rounded-lg border border-slate-200 object-contain"
                    />
                  </a>
                ) : (
                  <a
                    href={withToken(detail.attachment_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-brand-600 hover:underline"
                  >
                    <Icon.Attachment size={16} /> Buka lampiran ↗
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
